import { getTransportationType } from './transportationTypes';

const START_TIME_FIELDS = ['departure_time', 'pickup_time', 'start_time'];
const END_TIME_FIELDS = ['arrival_time', 'return_time'];

/**
 * Decide how ActivityModal should show Schedule vs category-specific times.
 * Canonical DB fields remain start_datetime / end_datetime; this only controls UX.
 */
export const getActivityScheduleMode = ({ category, transportationType } = {}) => {
  if (category === 'hotel') {
    return {
      hideSchedule: false,
      scheduleTitleKey: 'itinerary.scheduleLabel',
      startLabelKey: 'itinerary.checkIn',
      endLabelKey: 'itinerary.checkOut',
      source: 'hotel',
    };
  }

  if (category === 'transport' && transportationType === 'flight') {
    return {
      hideSchedule: true,
      source: 'flight_legs',
      hintKey: 'itinerary.scheduleFromFlight',
    };
  }

  if (category === 'transport' && transportationType) {
    const type = getTransportationType(transportationType);
    const fields = [
      ...(type?.fields?.required || []),
      ...(type?.fields?.optional || []),
    ];
    const startFields = fields.filter((name) => START_TIME_FIELDS.includes(name));
    const endFields = fields.filter((name) => END_TIME_FIELDS.includes(name));

    if (startFields.length && endFields.length) {
      return {
        hideSchedule: true,
        source: 'transport_fields',
        hintKey: 'itinerary.scheduleFromTransport',
        requiredTransportTimeFields: [...startFields, ...endFields],
      };
    }

    if (startFields.length && !endFields.length) {
      return {
        hideSchedule: false,
        hideStart: true,
        source: 'transport_start_only',
        scheduleTitleKey: 'itinerary.scheduleLabel',
        startLabelKey: 'itinerary.startDateTime',
        endLabelKey: 'itinerary.endDateTime',
        hintKey: 'itinerary.scheduleEndOnlyHint',
        requiredTransportTimeFields: startFields,
      };
    }
  }

  return {
    hideSchedule: false,
    scheduleTitleKey: 'itinerary.scheduleLabel',
    startLabelKey: 'itinerary.startDateTime',
    endLabelKey: 'itinerary.endDateTime',
    source: 'default',
  };
};

/**
 * Map category-specific times onto canonical start/end so saves work when
 * the Schedule UI is hidden or start is taken from pickup-only fields.
 */
export const deriveScheduleDatetimes = ({
  scheduleMode,
  transportationData = {},
  startDatetime = '',
  endDatetime = '',
} = {}) => {
  const data = transportationData || {};
  const firstValue = (fields) => fields.map((name) => data[name]).find(Boolean) || '';

  if (scheduleMode?.source === 'flight_legs') {
    return {
      start_datetime: data.departure_time || startDatetime || '',
      end_datetime: data.arrival_time || endDatetime || '',
    };
  }

  if (scheduleMode?.source === 'transport_fields') {
    return {
      start_datetime: firstValue(START_TIME_FIELDS) || startDatetime || '',
      end_datetime: firstValue(END_TIME_FIELDS) || endDatetime || '',
    };
  }

  if (scheduleMode?.source === 'transport_start_only') {
    return {
      start_datetime: firstValue(START_TIME_FIELDS) || startDatetime || '',
      end_datetime: endDatetime || '',
    };
  }

  return {
    start_datetime: startDatetime || '',
    end_datetime: endDatetime || '',
  };
};

/**
 * Fill missing required transport time fields from the activity schedule defaults
 * (e.g. day start/end) so Create isn't blocked by a collapsed Transportation Details panel.
 */
export const seedTransportTimesFromSchedule = ({
  transportationData = {},
  startDatetime = '',
  endDatetime = '',
  requiredFields = [],
} = {}) => {
  const next = { ...(transportationData || {}) };
  let changed = false;

  (requiredFields || []).forEach((fieldName) => {
    if (next[fieldName]) return;
    if (START_TIME_FIELDS.includes(fieldName) && startDatetime) {
      next[fieldName] = startDatetime;
      changed = true;
      return;
    }
    if (END_TIME_FIELDS.includes(fieldName) && endDatetime) {
      next[fieldName] = endDatetime;
      changed = true;
    }
  });

  return { transportationData: next, changed };
};

export const TRANSPORT_START_TIME_FIELDS = START_TIME_FIELDS;
export const TRANSPORT_END_TIME_FIELDS = END_TIME_FIELDS;
